<template>
  <div class="pa-4">
    <!-- Summary Stats -->
    <v-row>
      <v-col v-for="(stat, i) in summaryStats" :key="i" cols="12" md="3">
        <v-card class="pa-4" height="100%" outlined>
          <v-overlay :value="summary.loading" absolute>
            <v-progress-circular indeterminate size="36"/>
          </v-overlay>
          <div class="d-flex justify-space-between align-center">
            <div>
              <div class="text-subtitle-2">{{ stat.title }}</div>
              <div class="text-h6 font-weight-bold">{{ stat.value }}</div>
            </div>
            <v-icon :color="stat.color">{{ stat.icon }}</v-icon>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Chart Sections -->
    <v-row>
      <v-col v-for="(chart, key) in clinicCharts" :key="key" cols="12">
        <chart-section-v2
          :title="chart.title"
          :loading="chart.loading"
          :available-charts="chart.availableCharts"
          :active-key="chart.activeKey"
          :available-chart-keys="chart.availableChartKeys"
          @update:active-key="newType => handleActiveKeyUpdate(key, newType)"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import {
  getTotalStatsApi,
  getAppointmentsByMonthApi,
  getAppointmentsBySourceApi,
  getAppointmentsByProcessingResultApi,
  getAppointmentsByTreatmentApi, getTotalWebTrafficApi
} from "@/api";
import { mapGetters } from "vuex";
import ChartSectionV2 from "@/components/analytics/ChartSectionV2.vue";
import { buildWebTrafficViewsLineChart } from "@/utils/analysisToolRefactor";
import { formatMonthlyData, formatSourceData, formatProcessingResultData, formatTreatmentData } from "@/utils/summaryFormatters";


export default {
  name: "OnlineSummaryAnalysisTab",
  components: { ChartSectionV2 },
  props: {
    dateRange: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      summary: { loading: false, data: {} },
      webTraffic: { loading: false, data: [], activeKey: "line" },
      monthly: { loading: false, data: [], activeKey: "mixed" },
      source: { loading: false, data: [], activeKey: "mixed" },
      processing: { loading: false, data: [], activeKey: "mixed" },
      treatment: { loading: false, data: [], activeKey: "mixed" },
    };
  },
  computed: {
    ...mapGetters(["axiosParamData", 'clinicUser']),
    areaName() {
      return this.clinicUser ? '診所' : '醫師'
    },
    summaryStats() {
      const data = this.summary.data;
      return [
        { title: '總網頁瀏覽量', value: data.totalViews, icon: 'mdi-chart-line', color: 'deep-purple' },
        { title: '總預約數', value: data.totalAppointments, icon: 'mdi-calendar', color: 'blue' },
        { title: '新患者數', value: data.totalNewPatients, icon: 'mdi-account-multiple', color: 'green' },
        { title: '平均NP比例', value: data.avgNpRatio ? `${Number(data.avgNpRatio).toFixed(1)}%` : '0%', icon: 'mdi-pulse', color: 'orange' },
      ];
    },
    clinicCharts() {
      return {
        webTraffic: {
          title: `${this.areaName}網頁瀏覽量`,
          loading: this.webTraffic.loading,
          activeKey: this.webTraffic.activeKey,
          availableCharts: {
            line: buildWebTrafficViewsLineChart(this.webTraffic.data)
          }
        },
        monthly: {
          title: `${this.areaName}線上預約數`,
          loading: this.monthly.loading,
          activeKey: this.monthly.activeKey,
          availableChartKeys: ['mixed', 'line'],
          ...formatMonthlyData(this.monthly.data, {
            mixedName: 'NP結構',
            lineName:  '預約走勢',
          })
        },
        source: {
          title: `${this.areaName}線上預約來源`,
          loading: this.source.loading,
          activeKey: this.source.activeKey,
          availableChartKeys: ['mixed', 'line'],
          ...formatSourceData(this.source.data, {
            mixedName: '來源結構',
            lineName:  '來源走勢',
          })
        },
        treatment: {
          title: `${this.areaName}線上預約項目`,
          loading: this.treatment.loading,
          activeKey: this.treatment.activeKey,
          availableChartKeys: ['mixed', 'line'],
          ...formatTreatmentData(this.treatment.data, {
            mixedName: '項目結構',
            lineName:  '項目走勢',
          })
        },
        processing: {
          title: `${this.areaName}線上預約處理結果`,
          loading: this.processing.loading,
          activeKey: this.processing.activeKey,
          availableChartKeys: ['mixed', 'line'],
          ...formatProcessingResultData(this.processing.data, {
            mixedName: 'NP結構',
            lineName:  '處理結果走勢',
          })
        },
      }
    }
  },
  watch: {
    dateRange: {
      handler() { this.fetchAllData(); },
      immediate: true,
      deep: true
    }
  },
  methods: {
    handleActiveKeyUpdate(chartKey, newKey) {
      if (this[chartKey]) this[chartKey].activeKey = newKey;
    },
    fetchAllData() {
      if (!this.dateRange || this.dateRange.length !== 2) return;
      const [start, end] = [...this.dateRange].sort();
      const params = { ...this.axiosParamData, query_start: start, query_end: end };
      this.getSummaryData(params);
      this.getWebTrafficData(params);
      this.getMonthlyData(params);
      this.getSourceData(params);
      this.getProcessingResultData(params);
      this.getTreatmentData(params);
    },
// --- API Methods ---
    async getSummaryData(params) {
      this.summary.loading = true;
      const res = await getTotalStatsApi(params);
      this.summary.data = res.data.summary;
      this.summary.loading = false;
    },
    async getWebTrafficData(params) {
      this.webTraffic.loading = true;
      const res = await getTotalWebTrafficApi(params);
      this.webTraffic.data = res.data.webTraffic;
      this.webTraffic.loading = false;
    },
    async getMonthlyData(params) {
      this.monthly.loading = true;
      const res = await getAppointmentsByMonthApi(params);
      this.monthly.data = { mixed: res.data.byMonth, line: [] };
      this.monthly.loading = false;
    },
    async getSourceData(params) {
      this.source.loading = true;
      const res = await getAppointmentsBySourceApi(params);
      this.source.data = { mixed: res.data.bySource, line: res.data.bySourceLine };
      this.source.loading = false;
    },
    async getProcessingResultData(params) {
      this.processing.loading = true;
      const res = await getAppointmentsByProcessingResultApi(params);
      this.processing.data = { mixed: res.data.byProcessingResult || [], line: res.data.byProcessingResultLine || [] };
      this.processing.loading = false;
    },
    async getTreatmentData(params) {
      this.treatment.loading = true;
      const res = await getAppointmentsByTreatmentApi(params);
      this.treatment.data = { mixed: res.data.byTreatment || [], line: res.data.byTreatmentLine || [] };
      this.treatment.loading = false;
    }
  }
}
</script>
